import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Plus, ChevronRight, ClipboardList, Trash2,
  AlertTriangle, Filter, Stethoscope, X,
} from "lucide-react";

const STATUS_MAP = {
  aperto:               { cls: "bg-amber-500",   text: "text-amber-700",   label: "Aperto" },
  in_gestione:          { cls: "bg-blue-500",    text: "text-blue-700",    label: "In gestione" },
  inviato_specialista:  { cls: "bg-indigo-500",  text: "text-indigo-700",  label: "Inviato allo specialista" },
  in_attesa:            { cls: "bg-purple-500",  text: "text-purple-700",  label: "In attesa" },
  risposta_ricevuta:    { cls: "bg-teal-500",    text: "text-teal-700",    label: "Risposta ricevuta" },
  chiuso:               { cls: "bg-emerald-500", text: "text-emerald-700", label: "Chiuso" },
};

export default function CasiCliniciPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [counters, setCounters] = useState({});
  const [specializations, setSpecializations] = useState([]);
  const [redFlagsCount, setRedFlagsCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState(null);
  const [specFilter, setSpecFilter] = useState(null);
  const [rfFilter, setRfFilter] = useState(false);

  const load = async () => {
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (statusFilter) params.set("status", statusFilter);
      if (specFilter) params.set("specialization", specFilter);
      if (rfFilter) params.set("red_flag", "with");
      const { data } = await api.get(`/cases?${params.toString()}`);
      setItems(data.items || []);
      setCounters(data.counters || {});
      setSpecializations(data.specializations || []);
      setRedFlagsCount(data.red_flags_count || 0);
    } catch { toast.error("Impossibile caricare i casi"); setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, specFilter, rfFilter]);

  const clearAll = () => { setStatusFilter(null); setSpecFilter(null); setRfFilter(false); };
  const active = statusFilter || specFilter || rfFilter;

  const fmtDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric",
                                                    hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate("/dashboard")}
                  data-testid="cases-back-btn" className="h-10">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          <Button onClick={() => navigate("/nuova-valutazione")}
                  data-testid="cases-new-btn"
                  className="h-10 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuova valutazione clinica
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <ClipboardList className="h-5 w-5 text-blue-700" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Casi clinici</h1>
            <p className="text-sm text-slate-500">Tutti i tuoi casi con filtri per stato, sfera e priorità.</p>
          </div>
        </div>

        {/* PRIORITÀ: Red flag toggle grande */}
        <Card className={`p-3 rounded-2xl border-2 transition ${rfFilter ? "border-red-500 bg-red-50" : "border-red-200 bg-red-50/40"}`}>
          <button type="button"
                  onClick={() => setRfFilter(!rfFilter)}
                  data-testid="cases-filter-redflag"
                  className="w-full flex items-center gap-3 text-left">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0
                             ${rfFilter ? "bg-red-600 text-white" : "bg-red-100 text-red-700"}`}>
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-red-900 text-sm">
                Casi con red flag clinici {rfFilter ? "· ATTIVO" : ""}
              </div>
              <div className="text-[11px] text-red-700">
                {redFlagsCount} caso/i con almeno un red flag da attenzionare
              </div>
            </div>
            {rfFilter && <X className="h-4 w-4 text-red-600 flex-shrink-0" />}
          </button>
        </Card>

        {/* Filtri per stato */}
        <div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <Filter className="h-3 w-3" /> Filtra per stato
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setStatusFilter(null)}
                    data-testid="cases-filter-all"
                    className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border-2 transition ${statusFilter === null ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
              Tutti · {Object.values(counters).reduce((a, b) => a + b, 0)}
            </button>
            {Object.entries(STATUS_MAP).map(([k, s]) => (
              <button key={k} onClick={() => setStatusFilter(k)}
                      data-testid={`cases-filter-${k}`}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border-2 transition inline-flex items-center gap-1.5
                        ${statusFilter === k ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
                <span className={`h-2 w-2 rounded-full ${s.cls}`} />
                {s.label} · {counters[k] || 0}
              </button>
            ))}
          </div>
        </div>

        {/* Filtri per sfera clinica */}
        {specializations.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <Stethoscope className="h-3 w-3" /> Filtra per sfera clinica
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setSpecFilter(null)}
                      data-testid="cases-spec-all"
                      className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border-2 transition ${specFilter === null ? "bg-indigo-800 text-white border-indigo-800" : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300"}`}>
                Tutte
              </button>
              {specializations.map((s) => (
                <button key={s.specialization} onClick={() => setSpecFilter(s.specialization)}
                        data-testid={`cases-spec-${s.specialization.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border-2 transition
                          ${specFilter === s.specialization ? "bg-indigo-700 text-white border-indigo-700" : "bg-white text-indigo-800 border-indigo-200 hover:border-indigo-400"}`}>
                  {s.specialization} · {s.n}
                </button>
              ))}
            </div>
          </div>
        )}

        {active && (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
            <span className="text-[12px] text-blue-800">
              Filtri attivi: {items?.length || 0} caso/i visualizzati
            </span>
            <button onClick={clearAll}
                    data-testid="cases-filters-clear"
                    className="text-[11px] font-semibold text-blue-700 hover:underline inline-flex items-center gap-1">
              <X className="h-3 w-3" /> Rimuovi tutti i filtri
            </button>
          </div>
        )}

        {items === null ? (
          <div className="py-16 text-center text-slate-500">Caricamento…</div>
        ) : items.length === 0 ? (
          <Card className="p-10 rounded-2xl border-slate-200 bg-white text-center">
            <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500 mb-3">Nessun caso corrisponde a questi filtri.</div>
            <Button onClick={() => navigate("/nuova-valutazione")}
                    data-testid="cases-empty-new"
                    className="bg-blue-600 text-white">
              <Plus className="h-4 w-4 mr-2" /> Nuova valutazione clinica
            </Button>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="cases-list">
            {items.map((c, i) => {
              const s = STATUS_MAP[c.status] || STATUS_MAP.aperto;
              const spec = c.ai_classified_specialty?.specialization;
              return (
                <Card key={c.id} data-testid={`case-row-${i}`}
                      className={`p-3 rounded-xl bg-white hover:shadow-md transition
                                  ${c.has_red_flags ? "border-2 border-red-300 hover:border-red-500" : "border border-slate-200 hover:border-blue-400"}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div onClick={() => navigate(`/casi/${c.id}`)}
                         className="flex-1 cursor-pointer flex items-center gap-3 min-w-0">
                      <span className={`h-2.5 w-2.5 rounded-full ${s.cls} flex-shrink-0`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">
                            {c.patient_first_name} {c.patient_last_name}
                          </span>
                          <Badge className={`${s.text} bg-white border-slate-200 text-[11px]`}>
                            {s.label}
                          </Badge>
                          {c.has_red_flags && (
                            <Badge className="bg-red-100 text-red-800 border-red-300 text-[10px] inline-flex items-center gap-0.5">
                              <AlertTriangle className="h-2.5 w-2.5" /> RED FLAG
                            </Badge>
                          )}
                          {spec && (
                            <Badge className="bg-indigo-50 text-indigo-800 border-indigo-200 text-[10px]">
                              {spec}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {(c.reason_codes || []).join(", ") || "—"}
                          {c.reason_free_text && ` · ${c.reason_free_text.slice(0, 80)}`}
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-400">
                          Aggiornato: {fmtDate(c.updated_at)}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                    </div>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Cancellare definitivamente il caso di ${c.patient_first_name} ${c.patient_last_name}?`)) return;
                        try {
                          await api.delete(`/cases/${c.id}`);
                          toast.success("Caso cancellato"); load();
                        } catch { toast.error("Errore cancellazione"); }
                      }}
                      data-testid={`case-delete-${i}`}
                      className="h-8 w-8 rounded-md border border-slate-200 text-slate-400 hover:bg-red-50 hover:border-red-300 hover:text-red-600 flex items-center justify-center transition">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
